import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { setServer, setPort, setLogin, setPassword, resetConnectionForm } from '../../store/slices/mt5ConnectionSlice';
import MT5Service from '../../services/MT5Service';
import { MT5ConnectionRequest } from '../../services/types';

interface MT5ConnectionProps {
  onConnectionChange?: (connected: boolean) => void;
}

const MT5Connection: React.FC<MT5ConnectionProps> = ({ onConnectionChange }) => {
  const dispatch = useDispatch();
  const connectionForm = useSelector((state: RootState) => state.mt5Connection);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const mt5Service = new MT5Service();

  useEffect(() => {
    checkConnectionStatus();
    // Check connection status every 30 seconds
    const interval = setInterval(checkConnectionStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const checkConnectionStatus = async () => {
    try {
      const status = await mt5Service.getConnectionStatus();
      setIsConnected(status);
      onConnectionChange?.(status);
    } catch (error) {
      console.error('Failed to check connection status:', error);
      setIsConnected(false);
      onConnectionChange?.(false);
    }
  };

  const handleConnect = async () => {
    if (!connectionForm.login || !connectionForm.password) {
      setError('Please enter login and password');
      return;
    }

    setIsConnecting(true);
    setError('');
    setSuccess('');

    try {
      const connected = await mt5Service.initializeConnection(connectionForm);
      if (connected) {
        setIsConnected(true);
        setSuccess('Successfully connected to MT5!');
        onConnectionChange?.(true);
        // Clear password for security
        dispatch(setPassword(''));
      } else {
        setError('Failed to connect to MT5. Please check your credentials.');
      }
    } catch (error: any) {
      setError(error.message || 'Connection failed. Please try again.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await mt5Service.disconnect();
      setIsConnected(false);
      setSuccess('Disconnected from MT5');
      onConnectionChange?.(false);
      // Optionally reset form on disconnect
      // dispatch(resetConnectionForm());
    } catch (error: any) {
      setError('Failed to disconnect: ' + error.message);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Connection</h2>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
          {success}
        </div>
      )}

      {!isConnected ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Server
              </label>
              <input
                type="text"
                value={connectionForm.server}
                onChange={(e) => dispatch(setServer(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="demo.mt5.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Port
              </label>
              <input
                type="number"
                value={connectionForm.port}
                onChange={(e) => dispatch(setPort(parseInt(e.target.value)))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="443"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Login
              </label>
              <input
                type="text"
                value={connectionForm.login}
                onChange={(e) => dispatch(setLogin(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your MT5 login"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={connectionForm.password}
                onChange={(e) => dispatch(setPassword(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your MT5 password"
              />
            </div>
          </div>

          <button
            onClick={handleConnect}
            disabled={isConnecting}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isConnecting ? 'Connecting...' : 'Connect to MT5'}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-md p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">
                  Connected to MT5
                </h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Server: {connectionForm.server}:{connectionForm.port}</p>
                  <p>Login: {connectionForm.login}</p>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleDisconnect}
            className="w-full bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
          >
            Disconnect from MT5
          </button>
        </div>
      )}

      <div className="mt-6 pt-4 border-t border-gray-200">
        <h3 className="text-sm font-medium text-gray-700 mb-2">Connection Info</h3>
        <div className="text-xs text-gray-500 space-y-1">
          <p>• Make sure your MT5 terminal is running</p>
          <p>• Use demo account credentials for testing</p>
          <p>• Connection status is checked every 30 seconds</p>
          <p>• All trading operations require an active connection</p>
        </div>
      </div>
    </div>
  );
};

export default MT5Connection; 